(() => {
  'use strict';

  const routes = window.TCCRoutes || { root: '/', cart: '/cart', cartAdd: '/cart/add' };
  const quickview = document.querySelector('#QuickView');
  const quickviewContent = document.querySelector('#QuickViewContent');
  const escapeHtml = (value) => String(value ?? '').replace(/[&<>'"]/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' })[char]);
  const normalizeText = (value) => String(value || '').toLocaleLowerCase('fr-FR').normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const jsonEndpoint = (url) => url.endsWith('.js') ? url : `${url}.js`;
  const money = (cents) => (Number(cents || 0) / 100).toLocaleString('fr-FR', { style: 'currency', currency: routes.currency || 'EUR' });

  const productTitleLines = (title, modal = false) => {
    const parts = String(title || '').replace(/\s[–—-]\s/g, '|').split('|').slice(0, 3).map((part) => part.trim()).filter(Boolean);
    return `<span class="product-title-lines${modal ? ' product-title-lines--modal' : ''}">${parts.map((part) => `<span>${escapeHtml(part)}</span>`).join('')}</span>`;
  };

  const setMenu = (open) => {
    const button = document.querySelector('[data-menu]');
    const drawer = document.querySelector('[data-nav-drawer]');
    if (!button || !drawer) return;
    button.setAttribute('aria-expanded', String(open));
    button.setAttribute('aria-label', open ? 'Fermer le menu' : 'Ouvrir le menu');
    drawer.hidden = !open;
    document.body.classList.toggle('menu-open', open);
  };

  const activePanelFor = (browser) => browser?.querySelector('[data-panel]:not(.is-hidden)') || browser;

  const cardMatchesSeriesFamily = (card, family) => {
    if (!family) return true;
    const sets = String(card.dataset.sets || '').split('|').filter(Boolean).map((tag) =>
      normalizeText(tag).replace(/^set_/, '').replace(/[-_]+/g, ' ').replace(/['’]/g, '').trim()
    );
    if (family === 'vs') return sets.some((set) => set === 'pokemon vs' || set === 'theater limited vs pack');
    if (family === 'mcdonalds') return sets.some((set) => set.startsWith('mcdonald'));
    if (family === 'e-series') {
      const names = ['the town on no map', 'wind from the sea', 'split earth', 'mysterious mountains', 'pokemon e starter deck', 'pokemon e promos', 'pokemon e series', 'e series', 'e serie', 'expedition', 'aquapolis', 'skyridge'];
      return sets.some((set) => names.includes(set));
    }
    return false;
  };

  const filterCardBrowser = (browser) => {
    if (!browser) return;
    const panel = activePanelFor(browser);
    if (!panel) return;
    const search = panel.querySelector('[data-card-search]');
    const select = panel.querySelector('[data-set-filter]');
    const query = normalizeText(search?.value.trim());
    const serverSetFilter = Boolean(select?.hasAttribute('data-server-set-filter'));
    const set = serverSetFilter ? '' : select?.value || '';
    const family = panel.dataset.activeSeries || '';
    const filtering = Boolean(query || set || family);
    const expanded = panel.dataset.expanded === 'true';
    const cards = [...panel.querySelectorAll('[data-card]')];
    const hasHomeLimit = panel.hasAttribute('data-panel');
    let matched = 0;
    let visible = 0;

    cards.forEach((card) => {
      const title = normalizeText(card.dataset.title);
      const sets = card.dataset.sets || '';
      const match = (!query || title.includes(query)) && (!set || sets.includes(`|${set}|`)) && cardMatchesSeriesFamily(card, family);
      if (match) matched += 1;
      const beyondLimit = hasHomeLimit && match && !filtering && !expanded && matched > 12;
      const hidden = !match || beyondLimit;
      card.hidden = hidden;
      card.dataset.filterHidden = String(hidden);
      if (!hidden) visible += 1;
    });

    panel.querySelectorAll('[data-series-family]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.seriesFamily === family));
    });

    const empty = panel.querySelector('[data-empty-message]');
    if (empty) empty.hidden = matched !== 0;
    const status = panel.querySelector('[data-result-count]');
    if (status) {
      const serverTotal = Number(status.dataset.totalResults);
      const resultTotal = serverSetFilter && !query && Number.isFinite(serverTotal) ? serverTotal : matched;
      const noun = resultTotal > 1 ? 'cartes' : 'carte';
      status.textContent = hasHomeLimit && !filtering && !expanded && matched > 12 ? `${visible} ${noun} affichées sur ${matched}` : `${resultTotal} ${noun}`;
    }
    const moreWrap = panel.querySelector('[data-more-wrap]');
    const more = panel.querySelector('[data-show-more]');
    if (moreWrap) moreWrap.hidden = filtering || matched <= 12;
    if (more) {
      more.setAttribute('aria-expanded', String(expanded));
      more.innerHTML = expanded ? 'Voir moins de cartes <span aria-hidden="true">↑</span>' : 'Voir plus de cartes <span aria-hidden="true">↓</span>';
    }
  };

  const availableCardsFirst = (cards) => [
    ...cards.filter((card) => card.dataset.available === 'true'),
    ...cards.filter((card) => card.dataset.available !== 'true')
  ];

  const mapWithConcurrency = async (items, limit, mapper) => {
    const results = new Array(items.length);
    let nextIndex = 0;
    const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
      while (nextIndex < items.length) {
        const index = nextIndex;
        nextIndex += 1;
        results[index] = await mapper(items[index]);
      }
    });
    await Promise.all(workers);
    return results;
  };

  const fetchCollectionPageCards = async (browser, page) => {
    const url = new URL(window.location.href);
    url.hash = '';
    url.searchParams.set('page', String(page));
    url.searchParams.set('section_id', browser.dataset.sectionId);
    const response = await fetch(url.toString(), { headers: { Accept: 'text/html' } });
    if (!response.ok) throw new Error(`Page ${page} indisponible`);
    const markup = await response.text();
    const parsed = new DOMParser().parseFromString(markup, 'text/html');
    const grid = parsed.querySelector('[data-collection-grid]');
    if (!grid) throw new Error(`Grille absente sur la page ${page}`);
    return [...grid.children].filter((child) => child.matches('[data-card]'));
  };

  const initializeSoldLastCollection = async (browser) => {
    if (!browser || browser.dataset.soldLastInitialized === 'true') return;
    browser.dataset.soldLastInitialized = 'true';
    const grid = browser.querySelector('[data-collection-grid]');
    if (!grid) return;

    const pageSize = Number(browser.dataset.pageSize) || 24;
    const totalResults = Number(browser.querySelector('[data-result-count]')?.dataset.totalResults) || 0;
    const totalPages = Math.max(1, Math.ceil(totalResults / pageSize));
    const requestedPage = Number.parseInt(new URL(window.location.href).searchParams.get('page') || '1', 10);
    const currentPage = Math.min(Math.max(Number.isFinite(requestedPage) ? requestedPage : 1, 1), totalPages);
    const currentCards = [...grid.children].filter((child) => child.matches('[data-card]'));

    grid.append(...availableCardsFirst(currentCards));
    if (totalPages === 1 || currentCards.length === 0) return;

    browser.setAttribute('aria-busy', 'true');
    try {
      const pagesToFetch = Array.from({ length: totalPages }, (_, index) => index + 1).filter((page) => page !== currentPage);
      const fetchedPages = await mapWithConcurrency(pagesToFetch, 4, async (page) => [page, await fetchCollectionPageCards(browser, page)]);
      const cardsByPage = new Map([[currentPage, currentCards], ...fetchedPages]);
      const allCards = [];
      const seenProducts = new Set();

      for (let page = 1; page <= totalPages; page += 1) {
        (cardsByPage.get(page) || []).forEach((card, index) => {
          const productKey = card.dataset.productId || card.querySelector('.product-card__link')?.getAttribute('href') || `${page}-${index}`;
          if (seenProducts.has(productKey)) return;
          seenProducts.add(productKey);
          allCards.push(card);
        });
      }

      if (allCards.length < totalResults) throw new Error('Collection incomplète');
      const orderedCards = availableCardsFirst(allCards);
      const pageStart = (currentPage - 1) * pageSize;
      const pageCards = orderedCards.slice(pageStart, pageStart + pageSize);
      if (!pageCards.length && totalResults > 0) throw new Error('Page réorganisée vide');

      pageCards.forEach((card, index) => {
        const image = card.querySelector('.product-card__media img');
        if (image) image.loading = index < 4 ? 'eager' : 'lazy';
      });
      grid.replaceChildren(...pageCards);
      filterCardBrowser(browser);
    } catch (error) {
      console.warn('Le classement global des cartes vendues n’a pas pu être appliqué.', error);
    } finally {
      browser.removeAttribute('aria-busy');
    }
  };

  const filterFaq = (root) => {
    if (!root) return;
    const input = root.querySelector('[data-faq-search]');
    const query = normalizeText(input?.value.trim());
    const items = [...root.querySelectorAll('[data-faq-item]')];
    let visible = 0;

    items.forEach((item) => {
      const match = !query || normalizeText(item.dataset.faqContent).includes(query);
      item.hidden = !match;
      if (match) visible += 1;
    });

    root.querySelectorAll('[data-faq-category]').forEach((category) => {
      category.hidden = !category.querySelector('[data-faq-item]:not([hidden])');
    });

    const empty = root.querySelector('[data-faq-empty]');
    if (empty) empty.hidden = visible !== 0;
    const count = root.querySelector('[data-faq-count]');
    if (count) count.textContent = `${visible} réponse${visible > 1 ? 's' : ''} disponible${visible > 1 ? 's' : ''}`;
    const navigation = root.querySelector('.faq-category-nav');
    if (navigation) navigation.hidden = Boolean(query);
  };

  const setContactTopic = (root, requestedTopic) => {
    if (!root) return;
    const radios = [...root.querySelectorAll('[data-contact-topic]')];
    if (!radios.length) return;
    const selected = radios.find((radio) => radio.dataset.contactTopic === requestedTopic) || radios[0];
    selected.checked = true;

    const subject = root.querySelector('[data-contact-subject]');
    if (subject) subject.value = selected.dataset.contactLabel || selected.value;

    root.querySelectorAll('[data-contact-panel]').forEach((panel) => {
      const active = panel.dataset.contactPanel === selected.dataset.contactTopic;
      panel.hidden = !active;
      panel.querySelectorAll('input, select, textarea').forEach((field) => {
        field.disabled = !active;
        field.required = active && field.hasAttribute('data-contact-required');
      });
    });
  };

  const initializeContact = (root) => {
    if (!root || !root.querySelector('[data-contact-topic]')) return;
    let topic = root.querySelector('[data-contact-topic]:checked')?.dataset.contactTopic || 'general';

    if (root.dataset.contactInitialized !== 'true') {
      const parameters = new URLSearchParams(window.location.search);
      const requestedTopic = parameters.get('type');
      if (['general', 'product', 'sell', 'search', 'order'].includes(requestedTopic)) topic = requestedTopic;
      setContactTopic(root, topic);

      const productName = parameters.get('product');
      const productUrl = parameters.get('url');
      const productNameField = root.querySelector('[data-contact-product-name]');
      const productUrlField = root.querySelector('[data-contact-product-url]');
      if (productName && productNameField) productNameField.value = productName;
      if (productUrl && productUrlField) productUrlField.value = productUrl;
      root.dataset.contactInitialized = 'true';
      return;
    }

    setContactTopic(root, topic);
  };

  const initializeStoryImage = (scope) => {
    scope.querySelectorAll('[data-story-image-wrap]').forEach((wrap) => {
      const image = wrap.querySelector('img');
      const fallback = wrap.parentElement?.querySelector('[data-story-image-fallback]');
      if (!image || !fallback) return;
      const showFallback = () => {
        wrap.hidden = true;
        fallback.hidden = false;
      };
      image.addEventListener('error', showFallback, { once: true });
      if (image.complete && image.naturalWidth === 0) showFallback();
    });
  };

  const selectCategory = (button) => {
    const browser = button.closest('[data-card-browser]');
    const target = button.dataset.category;
    browser?.querySelectorAll('[data-category]').forEach((item) => {
      const active = item === button;
      item.classList.toggle('active', active);
      item.setAttribute('aria-selected', String(active));
    });
    browser?.querySelectorAll('[data-panel]').forEach((panel) => {
      const active = panel.dataset.panel === target;
      panel.classList.toggle('is-hidden', !active);
      panel.hidden = !active;
    });
    const panel = browser?.querySelector(`[data-panel="${target}"]`);
    if (panel) {
      const search = panel.querySelector('[data-card-search]');
      const select = panel.querySelector('[data-set-filter]');
      if (search) search.value = '';
      if (select) select.value = '';
      panel.dataset.activeSeries = '';
    }
    requestAnimationFrame(() => filterCardBrowser(browser));
  };

  const updateCartCount = async () => {
    try {
      const response = await fetch(jsonEndpoint(routes.cart), { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      document.querySelectorAll('[data-cart-count]').forEach((badge) => {
        badge.textContent = cart.item_count;
        badge.classList.toggle('is-empty', cart.item_count === 0);
      });
    } catch (_) {
      // The cart page remains the reliable fallback if the live counter cannot update.
    }
  };

  const openQuickview = async (trigger) => {
    if (!quickview || !quickviewContent) return;
    quickviewContent.innerHTML = '<div class="quickview-loading" role="status">Chargement de la carte…</div>';
    if (!quickview.open) quickview.showModal();

    try {
      const handle = encodeURIComponent(trigger.dataset.quickview);
      const response = await fetch(`${routes.root}products/${handle}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Produit indisponible');
      const product = await response.json();
      const images = (product.images?.length ? product.images : [product.featured_image]).filter(Boolean);
      const variant = product.variants?.find((item) => item.available) || product.variants?.[0];
      let current = 0;

      const render = () => {
        const image = images[current] || '';
        const available = Boolean(variant?.available);
        const galleryControls = images.length > 1 ? `<button type="button" data-prev aria-label="Photo précédente">←</button><button type="button" data-next aria-label="Photo suivante">→</button>` : '';
        const thumbs = images.length > 1 ? `<div class="qv-thumbs">${images.map((src, index) => `<button type="button" data-photo="${index}" class="${index === current ? 'active' : ''}" aria-label="Afficher la photo ${index + 1}" aria-pressed="${index === current}"><img src="${escapeHtml(src)}" alt=""></button>`).join('')}</div>` : '';
        quickviewContent.innerHTML = `
          <div class="qv-gallery">
            <div class="qv-main" data-product-zoom>
              <img class="product-zoom-image" src="${escapeHtml(image)}" alt="${escapeHtml(product.title)}">
              <span class="product-zoom-hint" aria-hidden="true">Survolez pour agrandir</span>
              ${galleryControls}<span class="qv-count">${current + 1} / ${images.length || 1}</span>
            </div>
            ${thumbs}
          </div>
          <div class="qv-copy">
            <small>Tokyo Collector Card</small>
            <h2>${productTitleLines(product.title, true)}</h2>
            <p class="qv-price">${money(variant?.price ?? product.price)}</p>
            <button class="qv-add" type="button" data-add="${variant?.id || ''}" ${available ? '' : 'disabled'}>${available ? 'Ajouter au panier' : 'Carte vendue'}</button>
            <a class="qv-link" href="${routes.root}products/${handle}">Voir tous les détails <span aria-hidden="true">→</span></a>
          </div>`;
      };

      render();
      quickviewContent.onclick = async (event) => {
        if (event.target.closest('[data-prev]')) { current = (current - 1 + images.length) % images.length; render(); return; }
        if (event.target.closest('[data-next]')) { current = (current + 1) % images.length; render(); return; }
        const thumb = event.target.closest('[data-photo]');
        if (thumb) { current = Number(thumb.dataset.photo); render(); return; }
        const add = event.target.closest('[data-add]');
        if (!add || !add.dataset.add) return;
        add.disabled = true;
        add.textContent = 'Ajout en cours…';
        try {
          const addResponse = await fetch(jsonEndpoint(routes.cartAdd), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: Number(add.dataset.add), quantity: 1 }] })
          });
          if (!addResponse.ok) throw new Error('Ajout impossible');
          add.textContent = 'Ajoutée au panier ✓';
          await updateCartCount();
        } catch (_) {
          add.disabled = false;
          add.textContent = 'Réessayer l’ajout';
        }
      };
    } catch (_) {
      quickviewContent.innerHTML = '<div class="quickview-error" role="alert"><h2>Aperçu indisponible</h2><p>Ouvrez la fiche de la carte pour consulter ses détails.</p></div>';
    }
  };

  const switchProductImage = (root, index) => {
    root.querySelectorAll('[data-product-slide]').forEach((slide) => { slide.hidden = Number(slide.dataset.productSlide) !== index; });
    root.querySelectorAll('[data-product-thumb]').forEach((thumb) => {
      const active = Number(thumb.dataset.productThumb) === index;
      thumb.classList.toggle('is-active', active);
      thumb.setAttribute('aria-pressed', String(active));
    });
  };

  const openPriceOffer = (trigger) => {
    const dialog = document.getElementById(trigger?.dataset.priceOfferOpen || '');
    if (!dialog) return;
    if (!dialog.open) {
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
    }
    requestAnimationFrame(() => dialog.querySelector('[data-price-offer-result], input:not([type="hidden"])')?.focus());
  };

  const closePriceOffer = (dialog) => {
    if (!dialog) return;
    if (typeof dialog.close === 'function') dialog.close();
    else dialog.removeAttribute('open');
  };

  const validateOfferAmount = (input) => {
    if (!input) return true;
    const floorCents = Number(input.dataset.offerFloorCents || 0);
    const amountCents = Number.isFinite(input.valueAsNumber) ? Math.round(input.valueAsNumber * 100) : 0;
    const tooLow = input.value !== '' && amountCents < floorCents;
    input.setCustomValidity(tooLow ? 'Cette proposition est trop éloignée du prix affiché. Merci de revoir votre montant.' : '');
    input.setAttribute('aria-invalid', String(tooLow));
    const error = input.closest('.price-offer-field')?.querySelector('[data-offer-amount-error]');
    if (error) error.hidden = !tooLow;
    return !tooLow;
  };

  document.addEventListener('click', (event) => {
    const menu = event.target.closest('[data-menu]');
    if (menu) { setMenu(menu.getAttribute('aria-expanded') !== 'true'); return; }
    if (event.target.closest('[data-nav-drawer] a')) setMenu(false);
    if (event.target.closest('[data-close]')) { quickview?.close(); return; }
    const seriesJump = event.target.closest('[data-series-jump]');
    if (seriesJump) {
      const browser = document.querySelector('#selection[data-card-browser]');
      const singleTab = browser?.querySelector('[data-category="single"]');
      if (singleTab) {
        event.preventDefault();
        selectCategory(singleTab);
        browser.querySelector('[data-panel="single"]').dataset.activeSeries = seriesJump.dataset.seriesJump;
        filterCardBrowser(browser);
        browser.scrollIntoView({ block: 'start', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      }
      return;
    }
    const seriesFamily = event.target.closest('[data-series-family]');
    if (seriesFamily) {
      const panel = seriesFamily.closest('[data-panel]');
      const browser = seriesFamily.closest('[data-card-browser]');
      if (panel && browser) {
        panel.dataset.activeSeries = seriesFamily.dataset.seriesFamily;
        const search = panel.querySelector('[data-card-search]');
        const select = panel.querySelector('[data-set-filter]');
        if (search) search.value = '';
        if (select) select.value = '';
        filterCardBrowser(browser);
      }
      return;
    }
    const category = event.target.closest('[data-category]');
    if (category) { selectCategory(category); return; }
    const more = event.target.closest('[data-show-more]');
    if (more) {
      const panel = more.closest('[data-panel]');
      panel.dataset.expanded = panel.dataset.expanded === 'true' ? 'false' : 'true';
      filterCardBrowser(more.closest('[data-card-browser]'));
      return;
    }
    const reviewsButton = event.target.closest('[data-reviews-more]');
    if (reviewsButton) {
      const section = reviewsButton.closest('.reviews-section');
      const expanded = section.classList.toggle('reviews-expanded');
      reviewsButton.setAttribute('aria-expanded', String(expanded));
      reviewsButton.innerHTML = expanded ? 'Voir moins d’avis <span aria-hidden="true">↑</span>' : 'Voir plus d’avis <span aria-hidden="true">↓</span>';
      return;
    }
    const offerOpen = event.target.closest('[data-price-offer-open]');
    if (offerOpen) { openPriceOffer(offerOpen); return; }
    const offerClose = event.target.closest('[data-price-offer-close]');
    if (offerClose) { closePriceOffer(offerClose.closest('[data-price-offer-dialog]')); return; }
    const offerDialog = event.target.closest('[data-price-offer-dialog]');
    if (offerDialog && event.target === offerDialog) { closePriceOffer(offerDialog); return; }
    const thumb = event.target.closest('[data-product-thumb]');
    if (thumb) { switchProductImage(thumb.closest('[data-product-root]'), Number(thumb.dataset.productThumb)); return; }
    const card = event.target.closest('[data-quickview]');
    if (card) openQuickview(card);
  });

  document.addEventListener('click', (event) => {
    const header = document.querySelector('[data-site-header]');
    const button = document.querySelector('[data-menu]');
    if (button?.getAttribute('aria-expanded') === 'true' && header && !header.contains(event.target)) setMenu(false);
  });

  document.addEventListener('input', (event) => {
    if (event.target.matches('[data-card-search]')) filterCardBrowser(event.target.closest('[data-card-browser]'));
    if (event.target.matches('[data-faq-search]')) filterFaq(event.target.closest('[data-faq-root]'));
    if (event.target.matches('[data-offer-amount]')) validateOfferAmount(event.target);
  });

  document.addEventListener('change', (event) => {
    if (event.target.matches('[data-set-filter]')) {
      if (event.target.hasAttribute('data-server-set-filter')) {
        const destination = new URL(event.target.value, window.location.origin);
        const current = new URL(window.location.href);
        current.searchParams.forEach((value, key) => {
          if (key !== 'page') destination.searchParams.append(key, value);
        });
        window.location.assign(destination.toString());
        return;
      }
      const panel = event.target.closest('[data-panel]');
      if (panel) panel.dataset.activeSeries = '';
      filterCardBrowser(event.target.closest('[data-card-browser]'));
    }
    if (event.target.matches('[data-contact-topic]')) setContactTopic(event.target.closest('[data-contact-root]'), event.target.dataset.contactTopic);
    if (event.target.matches('[data-sort-by]')) {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', event.target.value);
      window.location.assign(url.toString());
    }
    if (event.target.matches('[data-variant-select]')) {
      const option = event.target.selectedOptions[0];
      const root = event.target.closest('[data-product-root]');
      const available = option?.dataset.available === 'true';
      const price = root?.querySelector('[data-product-price]');
      const button = root?.querySelector('[data-add-to-cart]');
      if (price && option?.dataset.price) price.textContent = option.dataset.price;
      if (button) { button.disabled = !available; button.textContent = available ? 'Ajouter au panier' : 'Carte vendue'; }
      root?.querySelectorAll('[data-offer-current-price]').forEach((item) => { if (option?.dataset.price) item.textContent = option.dataset.price; });
      const offerPriceField = root?.querySelector('[data-offer-price-field]');
      const offerVariantField = root?.querySelector('[data-offer-variant-field]');
      const offerEntry = root?.querySelector('[data-price-offer-entry]');
      const offerAmount = root?.querySelector('[data-offer-amount]');
      if (offerPriceField && option?.dataset.price) offerPriceField.value = option.dataset.price;
      if (offerVariantField && option) offerVariantField.value = option.textContent.replace(/\s+[–—-]\s+Indisponible$/, '').trim();
      if (offerEntry) {
        const minimum = Number(offerEntry.dataset.priceOfferMinimum || 0);
        const variantPrice = Number(option?.dataset.priceCents || 0);
        offerEntry.hidden = !available || variantPrice < minimum;
        if (offerAmount) {
          const floorPercent = Number(offerEntry.dataset.priceOfferFloorPercent || 90);
          const floorCents = Math.ceil((variantPrice * floorPercent) / 100);
          offerAmount.dataset.offerFloorCents = String(floorCents);
          offerAmount.min = (floorCents / 100).toFixed(2).replace(/\.00$/, '');
          validateOfferAmount(offerAmount);
        }
      }
    }
  });

  document.addEventListener('submit', (event) => {
    const offerForm = event.target.closest?.('.price-offer-form');
    if (offerForm) {
      validateOfferAmount(offerForm.querySelector('[data-offer-amount]'));
      if (!offerForm.checkValidity()) return;
      const offerSubmit = offerForm.querySelector('.price-offer-submit');
      if (!offerSubmit) return;
      offerSubmit.disabled = true;
      offerSubmit.textContent = 'Envoi en cours…';
      return;
    }

    const contactForm = event.target.closest?.('.contact-form');
    if (!contactForm || !contactForm.checkValidity()) return;
    const contactSubmit = contactForm.querySelector('.contact-submit');
    if (!contactSubmit) return;
    contactSubmit.disabled = true;
    contactSubmit.textContent = 'Envoi en cours…';
  });

  document.addEventListener('pointermove', (event) => {
    if (event.pointerType === 'touch') return;
    const gallery = event.target.closest('[data-product-zoom]');
    if (!gallery) return;
    const image = gallery.querySelector('.product-zoom-image');
    if (!image) return;
    const rect = gallery.getBoundingClientRect();
    const x = Math.max(0, Math.min(100, ((event.clientX - rect.left) / rect.width) * 100));
    const y = Math.max(0, Math.min(100, ((event.clientY - rect.top) / rect.height) * 100));
    image.style.transformOrigin = `${x}% ${y}%`;
    gallery.classList.add('is-zoomed');
  });

  document.addEventListener('pointerout', (event) => {
    const gallery = event.target.closest?.('[data-product-zoom]');
    if (!gallery || gallery.contains(event.relatedTarget)) return;
    gallery.classList.remove('is-zoomed');
    const image = gallery.querySelector('.product-zoom-image');
    if (image) image.style.transformOrigin = '50% 50%';
  });

  document.addEventListener('keydown', (event) => { if (event.key === 'Escape') setMenu(false); });
  quickview?.addEventListener('click', (event) => { if (event.target === quickview) quickview.close(); });

  const initialize = (scope = document) => {
    scope.querySelectorAll('[data-card-browser]').forEach(filterCardBrowser);
    scope.querySelectorAll('[data-global-sold-last]').forEach((browser) => { void initializeSoldLastCollection(browser); });
    scope.querySelectorAll('[data-faq-root]').forEach(filterFaq);
    scope.querySelectorAll('[data-contact-root]').forEach(initializeContact);
    initializeStoryImage(scope);
    scope.querySelectorAll('[data-panel].is-hidden').forEach((panel) => { panel.hidden = true; });
    scope.querySelectorAll('[data-contact-result]').forEach((result) => requestAnimationFrame(() => result.focus()));
    scope.querySelectorAll('[data-price-offer-result]').forEach((result) => {
      const dialog = result.closest('[data-price-offer-dialog]');
      if (!dialog?.open) {
        if (typeof dialog?.showModal === 'function') dialog.showModal();
        else dialog?.setAttribute('open', '');
      }
      requestAnimationFrame(() => result.focus());
    });
  };

  document.addEventListener('DOMContentLoaded', () => initialize());
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();

/* TCC: recover the full collection series menu from public collection pages. */
(() => {
  'use strict';
  const prefix = 'SET_';
  const handleize = (value) => String(value).normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '').toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '-').replace(/-+/g, '-').replace(/^-+|-+$/g, '');
  const readTags = (root, tags) => {
    root.querySelectorAll('[data-card][data-sets]').forEach((card) => {
      String(card.dataset.sets || '').split('|').forEach((raw) => {
        const tag = raw.trim();
        if (tag.slice(0, 4).toUpperCase() === prefix) tags.set(tag.toLowerCase(), tag);
      });
    });
  };
  const updateMenu = (select, baseUrl, tags) => {
    const pathname = decodeURIComponent(window.location.pathname).replace(/\/+$/, '');
    const defaultOption = select.options[0]?.cloneNode(true);
    if (!defaultOption) return;
    const options = [...tags.values()]
      .sort((a, b) => a.slice(4).localeCompare(b.slice(4), 'fr', {sensitivity:'base', numeric:true}))
      .map((tag) => {
        const option = document.createElement('option');
        option.value = `${baseUrl.pathname.replace(/\/+$/, '')}/${handleize(tag)}`;
        option.textContent = tag.slice(4).replace(/_/g, ' ').replace(/\s+/g, ' ').trim();
        option.selected = pathname.toLowerCase() === option.value.toLowerCase();
        return option;
      });
    defaultOption.selected = !options.some((option) => option.selected);
    select.replaceChildren(defaultOption, ...options);
  };
  const populateSeries = async (select) => {
    if (select.dataset.seriesRecoveryStarted === 'true') return;
    select.dataset.seriesRecoveryStarted = 'true';
    const baseValue = select.options[0]?.value;
    const browser = select.closest('[data-card-browser]');
    if (!baseValue || !browser) return;
    const baseUrl = new URL(baseValue, window.location.origin);
    if (baseUrl.origin !== window.location.origin) return;
    baseUrl.hash = '';
    const tags = new Map();
    readTags(browser, tags);
    [...select.options].slice(1).forEach((option) => {
      // Preserve server-rendered options until the complete list is recovered.
      // The server may already provide a complete list on another theme version.
      const label = option.textContent.trim();
      if (label) tags.set(`${prefix}${label}`.toLowerCase(), `${prefix}${label}`);
    });
    if (select.options.length < 2) updateMenu(select, baseUrl, tags);
    try {
      const fetchPage = async (page) => {
        const url = new URL(baseUrl);
        url.searchParams.set('page', String(page));
        const response = await fetch(url.toString(), {headers:{Accept:'text/html'}});
        if (!response.ok) throw new Error(`Collection page ${page}: ${response.status}`);
        const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (!parsed.querySelector('[data-collection-grid]')) throw new Error('Collection grid unavailable');
        return parsed;
      };
      // Read the server page: the sold-last display may already have moved cards.
      const firstPage = await fetchPage(1);
      const total = Number(firstPage.querySelector('[data-result-count]')?.dataset.totalResults) || 0;
      const pageSize = Number(firstPage.querySelector('[data-card-browser]')?.dataset.pageSize) || 24;
      const totalPages = Math.max(1, Math.ceil(total / pageSize));
      const completeTags = new Map();
      readTags(firstPage, completeTags);
      // Four requests at a time, regardless of catalogue size.
      for (let page = 2; page <= totalPages; page += 4) {
        const pages = Array.from({length:Math.min(4, totalPages - page + 1)}, (_, i) => page + i);
        const parsedPages = await Promise.all(pages.map(fetchPage));
        parsedPages.forEach((parsed) => readTags(parsed, completeTags));
      }
      updateMenu(select, baseUrl, completeTags);
      select.dataset.seriesRecoveryComplete = 'true';
    } catch (error) {
      console.warn('Le menu des séries utilise les options disponibles sur cette page.', error);
    }
  };
  const initialize = (scope = document) => {
    scope.querySelectorAll('[data-set-filter][data-server-set-filter]').forEach(populateSeries);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize());
  else initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();

